import type { BridgeChartInstance } from "../core/chart";
import type { BridgeChartPeriod } from "../types/public";

const periods = new Set(["1M", "3M", "6M", "1Y", "2Y", "ALL"]);

// Progressive enhancement only: native links remain usable without a mounted chart.
export function bindWorkspacePeriods(container: HTMLElement, chart: BridgeChartInstance): () => void {
  const workspace = container.closest?.<HTMLElement>("[data-workspace-symbol][data-workspace-timeframe]");
  const view = container.ownerDocument.defaultView;
  if (container.dataset.chartContext !== "workspace" || !workspace || !view) return () => {};
  let disposed = false;
  const bars = chart.data.bars, symbol = chart.data.symbol, timeframe = chart.data.timeframe;
  let lastPeriod = chart.toolbarState.period;
  const notice = container.querySelector<HTMLElement>("[data-chart-period-navigation]");
  const reload = notice?.querySelector<HTMLAnchorElement>("a");
  const currentUrl = () => {
    const url = new URL(view.location.href);
    const single = (key: string) => url.searchParams.getAll(key).length <= 1;
    if (url.origin !== view.location.origin || url.pathname !== "/charts" || url.username || url.password
        || !single("symbol") || !single("timeframe") || !single("period")
        || url.searchParams.get("symbol")?.trim().toUpperCase() !== symbol.toUpperCase()
        || (url.searchParams.get("timeframe") ?? "1D").trim().toUpperCase() !== timeframe
        || workspace.dataset.workspaceSymbol !== symbol || workspace.dataset.workspaceTimeframe !== timeframe
        || chart.data.bars !== bars || chart.data.symbol !== symbol || chart.data.timeframe !== timeframe
        || chart.data.metadata.symbol !== symbol || chart.data.metadata.timeframe !== timeframe) return null;
    return url;
  };
  const navigate = (period: BridgeChartPeriod) => {
    const url = currentUrl();
    if (!url) return false;
    url.searchParams.set("period", period);
    if (reload) reload.href = url.href;
    try {
      view.history.replaceState(view.history.state, "", url);
      if (notice) notice.hidden = true;
      return true;
    } catch {
      // The local viewport remains usable; expose an ordinary same-context GET link.
      if (notice) notice.hidden = false;
      return false;
    }
  };
  const matches = (url: URL) => url.origin === view.location.origin && url.pathname === "/charts"
    && url.searchParams.getAll("symbol").length === 1 && url.searchParams.get("symbol") === chart.data.metadata.symbol
    && url.searchParams.getAll("timeframe").length === 1 && url.searchParams.get("timeframe") === chart.data.timeframe
    && url.searchParams.getAll("period").length === 1 && periods.has(url.searchParams.get("period")!);
  const sync = () => {
    if (disposed || !currentUrl()) {
      if (notice) notice.hidden = true;
      if (reload) reload.removeAttribute("href");
      return;
    }
    const period = chart.toolbarState.period;
    if (period !== lastPeriod) {
      lastPeriod = period;
      navigate(period);
    }
    workspace.dataset.workspacePeriod = period;
    const input = workspace.querySelector<HTMLInputElement>('.chart-terminal-search input[name="period"]');
    if (input) input.value = period;
    container.querySelectorAll<HTMLAnchorElement>(".bridge-chart-periods a[href]").forEach(link => {
      try {
        const url = new URL(link.href, view.location.href);
        if (!matches(url)) return;
        if (url.searchParams.get("period") === period) link.setAttribute("aria-current", "page");
        else link.removeAttribute("aria-current");
      } catch { /* An invalid link keeps its native behavior. */ }
    });
  };
  const click = (event: MouseEvent) => {
    if (disposed || event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const link = (event.target as Element | null)?.closest?.<HTMLAnchorElement>(".bridge-chart-periods a[href]");
    if (!link || !container.contains(link) || link.hasAttribute("target") || link.hasAttribute("download")) return;
    try {
      const url = new URL(link.href, view.location.href);
      if (!matches(url) || url.hash || url.username || url.password) return;
      if (!currentUrl()) return;
      // Keep unrelated query state. Enhanced period changes replace this entry;
      // they do not create history entries or require a popstate router.
      const period = url.searchParams.get("period") as BridgeChartPeriod;
      if (!navigate(period)) { if (notice) notice.hidden = true; return; }
      lastPeriod = period;
      chart.setPeriod(period);
      event.preventDefault();
    } catch { /* Failed enhancement falls back to the original href. */ }
  };
  container.addEventListener("click", click);
  const offToolbar = chart.on("toolbar", sync);
  const dispose = () => {
    if (disposed) return;
    disposed = true;
    container.removeEventListener("click", click);
    if (notice) notice.hidden = true;
    if (reload) reload.removeAttribute("href");
    offToolbar();
    offDestroy();
  };
  const offDestroy = chart.on("destroy", dispose);
  sync();
  return dispose;
}
